import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { TextSectionSlide } from '@/types';
import { getUploadUrl } from '@/config/api';
import TextOverlay from './TextOverlay';
import { useCursorContext } from '@/providers/CursorProvider';
import RotateDeviceIndicator from '@/components/RotateDeviceIndicator';

interface TextSectionProps {
    slide: TextSectionSlide;
    isActive?: boolean;
}

const TextSection: React.FC<TextSectionProps> = ({ slide, isActive }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [isInNavigationZone, setIsInNavigationZone] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const { setCursorType } = useCursorContext();

    const handleImageClick = (e: React.MouseEvent) => {
        if (slide.image_url && !isDragging) {
            if (isInNavigationZone) return;
            window.open(slide.image_url, '_blank', 'noopener,noreferrer');
        }
    };

    useEffect(() => {
        const checkMobile = () => setIsMobile(window.innerWidth <= 768);
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const backgroundStyle: React.CSSProperties = {
        width: '100%',
        height: '100vh',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: slide.background_color || '#000',
        overflow: 'hidden'
    };

    // Get background media URL - handle both transformed and raw Strapi formats
    const getBackgroundMediaUrl = () => {
        if (!slide.background_media) return null;

        // Check if it's already transformed (has url directly)
        if (slide.background_media.url) {
            return getUploadUrl(slide.background_media.url);
        }

        // Check if it's raw Strapi format (data.attributes)
        if ((slide.background_media as any).data?.attributes?.url) {
            return getUploadUrl((slide.background_media as any).data.attributes.url);
        }

        return null;
    };

    const backgroundMediaUrl = getBackgroundMediaUrl();

    return (
        <Box sx={backgroundStyle}>
            {/* Background Media */}
            {slide.show_background && backgroundMediaUrl && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0.3,
                        zIndex: 0
                    }}
                >
                    {slide.image_url ? (
                        <div onClick={handleImageClick} style={{ display: 'block', width: '100%', height: '100%', cursor: 'default' }}>
                            <img
                                src={backgroundMediaUrl}
                                alt="background"
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: window.innerWidth <= 768 ? 'contain' : 'cover'
                                }}
                            />
                        </div>
                    ) : (
                        <img
                            src={backgroundMediaUrl}
                            alt="background"
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: window.innerWidth <= 768 ? 'contain' : 'cover'
                            }}
                        />
                    )}
                </Box>
            )}

            {/* Navigation Zones */}
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => setIsInNavigationZone(false)}
                sx={{ position: 'absolute', left: 0, top: 0, width: '200px', height: '100%', zIndex: 10, pointerEvents: 'auto' }}
            />
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => setIsInNavigationZone(false)}
                sx={{ position: 'absolute', right: 0, top: 0, width: '200px', height: '100%', zIndex: 10, pointerEvents: 'auto' }}
            />
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => setIsInNavigationZone(false)}
                sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '200px', zIndex: 10, pointerEvents: 'auto' }}
            />
            <Box
                onMouseEnter={() => {
                    setIsInNavigationZone(true);
                    setCursorType('default');
                }}
                onMouseLeave={() => setIsInNavigationZone(false)}
                sx={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: '200px', zIndex: 10, pointerEvents: 'auto' }}
            />

            {/* Text Content - directly render TextOverlay without wrapper */}
            <TextOverlay
                text_content={isMobile ? (slide.mobile_text_content || '') : slide.text_content}
                isActive={isActive}
            />
            
            <RotateDeviceIndicator show={slide.enable_rotate && isActive} />
        </Box>
    );
};

export default TextSection;
